import React, {useEffect, useState} from 'react';
import {Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, Paper, Typography, useMediaQuery, useTheme} from '@mui/material';
import {Add, Close} from '@mui/icons-material';
import Spinner from '../../spinner/Spinner';
import {CustomSnackbar} from '../../alert/CustomSnackbar';
import {getSettingsColors, settingsInputStyles} from '../SettingsDesign';
import DomainAlert from './DomainAlert';

export default function DomainManager({kind, description, data = [], Form, List, load, resetDomain, ...props}) {
    const theme = useTheme();
    const colors = getSettingsColors(theme);
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [submittedDomain, setSubmittedDomain] = useState('');
    const {errorMsg, successMsg, isLoading} = props.state;

    useEffect(() => { load(); }, [load]);
    const startCreate = () => {
        props.clearErrors();
        props.setActiveStep(0);
        resetDomain('');
        setFeedback(null);
        setSubmittedDomain('');
        setOpen(true);
    };
    const close = () => {
        if (!isLoading) {
            setOpen(false);
            setFeedback(null);
            props.clearErrors();
        }
    };
    const verify = async domain => {
        props.clearErrors();
        setFeedback(null);
        setSubmittedDomain(domain);
        const result = await props.addAndVerify(domain);
        setFeedback({severity: result.success ? 'success' : 'error', message: result.message});
    };
    const added = feedback?.severity === 'success';
    const title = `Add ${kind} domain`;

    return <>
        <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2}}>
            <Box>
                <Typography component="h2" sx={{fontSize: 18, fontWeight: 600, textTransform: 'capitalize'}}>{kind} domains</Typography>
                <Typography sx={{mt: 0.5, color: 'text.secondary', fontSize: 13}}>{description}</Typography>
            </Box>
            <Button variant="contained" startIcon={<Add/>} onClick={startCreate} disabled={isLoading} sx={{borderRadius: '8px'}}>Add {kind} domain</Button>
        </Box>
        <Paper elevation={0} sx={{position: 'relative', bgcolor: colors.card, border: '1px solid', borderColor: colors.border,
            borderRadius: '12px', overflow: 'hidden'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, px: {xs: 2, md: 2.5}, py: 2}}>
                <Typography sx={{fontSize: 15, fontWeight: 600}}>Configured domains</Typography>
                <Chip label={data.length} size="small" sx={{height: 24, bgcolor: colors.inset, color: colors.muted, borderRadius: '6px'}}/>
            </Box>
            {isLoading && !open && <Spinner size={32} overComponentBox/>}
            <List data={data} disabled={isLoading}/>
        </Paper>
        {!open && errorMsg && <CustomSnackbar message={errorMsg} clear={props.clearErrors}/>}
        {!open && successMsg && <CustomSnackbar message={successMsg} severity="success" clear={props.clearErrors}/>}
        <Dialog open={open} onClose={close} fullWidth maxWidth="sm" fullScreen={mobile} aria-labelledby={`${kind}-dialog-title`}
                slotProps={{paper: {sx: {...settingsInputStyles, bgcolor: colors.card, color: colors.text,
                    border: theme.palette.mode === 'dark' ? '1px solid' : 0, borderColor: colors.border,
                    borderRadius: {xs: 0, sm: '16px'}, backgroundImage: 'none',
                    '& .MuiOutlinedInput-notchedOutline': {border: 0}, '& .MuiButton-root': {borderRadius: '8px'},
                    '& .MuiTypography-root': {whiteSpace: 'normal'}, '& .MuiStepLabel-label': {fontWeight: 600}, '& .MuiStepContent-root': {borderColor: colors.border},
                    '& .MuiStepConnector-line': {borderColor: colors.border}}}}}>
            <DialogTitle component="div" sx={{px: {xs: 2, sm: 3}, pt: 3, pb: 2}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                    <Typography component="h2" id={`${kind}-dialog-title`} sx={{fontSize: 22, fontWeight: 700}}>{title}</Typography>
                    <IconButton aria-label="Close domain setup" onClick={close} disabled={isLoading}><Close fontSize="small"/></IconButton>
                </Box>
                <Typography sx={{fontSize: 13, color: 'text.secondary', mt: 0.5}}>Connect your domain in three steps, then verify its DNS record.</Typography>
            </DialogTitle>
            <DialogContent sx={{px: {xs: 2, sm: 3}, pb: 1, scrollbarGutter: 'stable'}}>
                {added ? <>
                    <DomainAlert severity="success">{feedback.message}</DomainAlert>
                    <Typography sx={{fontSize: 14, overflowWrap: 'anywhere'}}>https://{submittedDomain}</Typography>
                    <Typography sx={{fontSize: 13, color: 'text.secondary', mt: 0.5}}>Your domain is verified and ready to use in your campaigns.</Typography>
                </> : <Box component="fieldset" disabled={isLoading} sx={{border: 0, p: 0, m: 0, minWidth: 0, fontSize: 14, lineHeight: 1.7}}>
                    <Form {...props} addAndVerify={verify} submissionError={feedback?.severity === 'error' ? feedback.message : ''}/>
                </Box>}
            </DialogContent>
            <DialogActions sx={{px: {xs: 2, sm: 3}, py: 2, gap: 1}}>
                {isLoading && <Box role="status" sx={{display: 'flex', alignItems: 'center', gap: 1, mr: 'auto', fontSize: 13, color: 'text.secondary'}}>
                    <CircularProgress size={16}/>Verifying DNS…
                </Box>}
                <Button color={added ? "primary" : "inherit"} variant={added ? "contained" : "text"} onClick={close} disabled={isLoading}>{added ? "Done" : "Cancel"}</Button>
            </DialogActions>
        </Dialog>
    </>;
}
